import { useState, useEffect } from 'react'
import api from '../utils/api'
import '../styles/createStoryModal.css'

interface Contact {
  email: string
  prenom?: string
  nom?: string
}

interface CreateStoryModalProps {
  onClose: () => void
  onCreated: () => void
  userEmail: string
}

export default function CreateStoryModal({ onClose, onCreated, userEmail }: CreateStoryModalProps) {
  const [step, setStep] = useState<'content' | 'visibility'>('content')
  const [content, setContent] = useState('')
  const [mediaType, setMediaType] = useState('text')
  const [previewUrl, setPreviewUrl] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [visibilityType, setVisibilityType] = useState<'public' | 'private' | 'whitelist' | 'blacklist'>('public')
  const [selectedContacts, setSelectedContacts] = useState<string[]>([])
  const [allContacts, setAllContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const loadContacts = async () => {
    try {
      const response = await api.get('/api/contacts')
      setAllContacts(response.data.filter((c: Contact) => c.email !== userEmail))
    } catch (err) {
      console.error('Erreur lors du chargement des contacts:', err)
    }
  }

  useEffect(() => {
    loadContacts()
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setSelectedFile(file)
    
    const reader = new FileReader()
    reader.onload = (event) => {
      const result = event.target?.result as string
      setPreviewUrl(result)
      
      // Déterminer le type de media
      if (file.type.startsWith('image/')) {
        setMediaType('image')
      } else if (file.type.startsWith('video/')) {
        setMediaType('video')
      }
    }
    reader.readAsDataURL(file)
  }

  const handleNext = () => {
    if (!content && !selectedFile) {
      setError('Veuillez ajouter du contenu ou un fichier')
      return
    }
    setError('')
    setStep('visibility')
  }

  const handleContactToggle = (email: string) => {
    setSelectedContacts(prev => 
      prev.includes(email) 
        ? prev.filter(e => e !== email)
        : [...prev, email]
    )
  }

  const handleCreate = async () => {
    try {
      setLoading(true)
      setError('')

      const formData = new FormData()
      formData.append('content', content)
      formData.append('mediaType', mediaType)
      formData.append('visibilityType', visibilityType)
      formData.append('visibilityList', JSON.stringify(selectedContacts))
      
      // Ajouter le fichier si présent
      if (selectedFile) {
        formData.append('file', selectedFile)
      }

      try {
        await api.post('/api/stories', formData)
        onCreated()
      } catch (err: any) {
        // Check if error is about visibility_type column
        const errorMsg = err.response?.data?.error || '';
        if (errorMsg.includes('visibility_type') || errorMsg.includes('no column named')) {
          console.log('🔧 Detected schema error, attempting migration...');
          
          // Try to fix the database schema
          try {
            const fixResponse = await api.get('/api/debug/fix-stories');
            console.log('✅ Migration result:', fixResponse.data);
            
            // Retry the story creation
            console.log('🔄 Retrying story creation...');
            await api.post('/api/stories', formData);
            console.log('✅ Story created successfully after migration');
            onCreated();
          } catch (fixErr: any) {
            console.error('❌ Migration failed:', fixErr);
            setError('Erreur lors de la préparation de la base de données. Veuillez réessayer.');
          }
        } else {
          setError(errorMsg || 'Erreur lors de la création de la story');
        }
      }
    } catch (err: any) {
      console.error('Unexpected error:', err);
      setError('Une erreur inattendue s\'est produite');
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="story-modal-overlay" onClick={onClose}>
      <div className="story-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>✕</button>

        {step === 'content' ? (
          // Step 1: Content
          <div className="story-step">
            <h2>Créer une story</h2>

            {/* Text input */}
            <textarea 
              className="story-text-input"
              placeholder="Écris un message (optionnel)..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={500}
            />
            <div className="char-count">{content.length}/500</div>

            {/* File upload */}
            <div className="file-upload-section">
              <label className="file-upload-label">
                <input 
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*,video/*"
                />
                <span className="file-upload-btn">📎 Ajouter un fichier</span>
              </label>
            </div>

            {/* Preview */}
            {previewUrl && (
              <div className="preview-section">
                <div className="preview-label">Aperçu:</div>
                {mediaType === 'image' ? (
                  <img src={previewUrl} alt="Preview" className="preview-image" />
                ) : mediaType === 'video' ? (
                  <video src={previewUrl} className="preview-video" controls />
                ) : null}
                <button 
                  className="btn-remove-file"
                  onClick={() => {
                    setPreviewUrl('')
                    setSelectedFile(null)
                    setMediaType('text')
                  }}
                >
                  Supprimer
                </button>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            <button 
              className="btn-next"
              onClick={handleNext}
            >
              Suivant
            </button>
          </div>
        ) : (
          // Step 2: Visibility
          <div className="story-step">
            <h2>Qui peut voir?</h2>

            {/* Visibility type selection */}
            <div className="visibility-options">
              <label className="visibility-option">
                <input 
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={visibilityType === 'public'}
                  onChange={(e) => {
                    setVisibilityType(e.target.value as any)
                    setSelectedContacts([])
                  }}
                />
                <span className="visibility-icon">🌍</span>
                <span>Public - Tous tes contacts</span>
              </label>

              <label className="visibility-option">
                <input 
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={visibilityType === 'private'}
                  onChange={(e) => {
                    setVisibilityType(e.target.value as any)
                    setSelectedContacts([])
                  }}
                />
                <span className="visibility-icon">🔒</span>
                <span>Privé - Personne d'autre</span>
              </label>

              <label className="visibility-option">
                <input 
                  type="radio"
                  name="visibility"
                  value="whitelist"
                  checked={visibilityType === 'whitelist'}
                  onChange={(e) => setVisibilityType(e.target.value as any)}
                />
                <span className="visibility-icon">✅</span>
                <span>Seulement certains contacts</span>
              </label>

              <label className="visibility-option">
                <input 
                  type="radio"
                  name="visibility"
                  value="blacklist"
                  checked={visibilityType === 'blacklist'}
                  onChange={(e) => setVisibilityType(e.target.value as any)}
                />
                <span className="visibility-icon">❌</span>
                <span>Tous sauf certains contacts</span>
              </label>
            </div>

            {/* Contact selection */}
            {(visibilityType === 'whitelist' || visibilityType === 'blacklist') && (
              <div className="contacts-section">
                <h3>
                  {visibilityType === 'whitelist' 
                    ? 'Contacts qui peuvent voir'
                    : 'Contacts qui ne peuvent pas voir'
                  }
                </h3>
                <div className="contacts-list">
                  {allContacts.map((contact) => (
                    <label key={contact.email} className="contact-item">
                      <input 
                        type="checkbox"
                        checked={selectedContacts.includes(contact.email)}
                        onChange={() => handleContactToggle(contact.email)}
                      />
                      <span className="contact-name">
                        {contact.prenom} {contact.nom}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            <div className="modal-actions">
              <button 
                className="btn-back"
                onClick={() => setStep('content')}
              >
                Retour
              </button>
              <button 
                className="btn-create"
                onClick={handleCreate}
                disabled={loading}
              >
                {loading ? 'Création...' : 'Publier'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
